import React, { useState, useRef, useEffect } from "react";
import { FaChevronDown } from "react-icons/fa";
import { FaClock } from "react-icons/fa6";

export type Option = {
  label: string;
  value: string;
};

type Props = {
  options: Option[];
  selected: string;
  onChange: (value: string) => void;
  placeholder?: string;
};

const normalize = (s = "") => s.toString().toLowerCase().replace(/\s+/g, "");

export default function MarketTimeDropdown({
  options,
  selected,
  onChange,
  placeholder = "Select time...",
}: Props) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const rootRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!open) setSearch("");
  }, [selected, open]);

  useEffect(() => {
    const handlePointerDown = (e: PointerEvent) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target as Node)) {
        if (open && search.trim() && search !== selected) {
          onChange(search.trim());
        }
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("pointerdown", handlePointerDown, true);
    return () =>
      document.removeEventListener("pointerdown", handlePointerDown, true);
  }, [open, search, selected, onChange]);

  const openDropdown = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setSearch("");
    setOpen(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleSelect = (val: string) => {
    onChange(val);
    setOpen(false);
    setSearch("");
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const val = search.trim();
      if (val) onChange(val);
      setOpen(false);
      setSearch("");
    }
  };

  const filtered =
    open && search
      ? options.filter((opt) =>
          normalize(opt.label).includes(normalize(search))
        )
      : options;

  return (
    <div ref={rootRef} className="relative  text-sm">
      <div
        className="border rounded-md flex items-center px-2 py-2 cursor-pointer justify-between bg-white"
        onClick={openDropdown}
      >
        {open ? (
          <input
            ref={inputRef}
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="flex-1 outline-none text-sm color-text-primary"
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <span className="color-blue">{selected || placeholder}</span>
        )}
        {/* <FaChevronDown
          className={`ml-2 transition-transform ${open ? "rotate-180" : ""}`}
          size={12}
          color="#16a34a"
        /> */}
        <FaClock size={12} color="#0066FF" />
      </div>

      {open && (
        <div className="absolute z-50 bg-white border rounded-md w-full mt-1 shadow-lg">
          <div className="max-h-48 overflow-auto text-sm">
            {filtered.length > 0 ? (
              filtered.map((opt) => (
                <div
                  key={opt.value}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelect(opt.value);
                  }}
                  className={`px-3 py-2 cursor-pointer hover:bg-[#D7F3DD] color-blue ${
                    selected === opt.value ? "bg-[#D7F3DD] font-medium" : ""
                  }`}
                >
                  {opt.label}
                </div>
              ))
            ) : (
              <div className="px-3 py-3 text-center color-sub-text text-sm">
                No results
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
